import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { IMG, http } from "@/lib/api";
import { useStore } from "@/lib/store";
import { Reveal } from "@/components/site/Reveal";
import { LogoMark } from "@/components/brand/Logo";

const FALLBACK = [
  { slug: "sunset-citrus-glow", name: "Sunset Citrus Glow", chapter: "Morning Light", color: "#E58A2C", ingredients: ["Orange", "Grapefruit", "Carrot", "Ginger"] },
  { slug: "garden-green-tonic", name: "Garden Green Tonic", chapter: "Greens & Grounding", color: "#5E7153", ingredients: ["Celery", "Cucumber", "Kale", "Apple"] },
  { slug: "canyon-beet-bloom", name: "Canyon Beet Bloom", chapter: "Root & Roots", color: "#9B2C3B", ingredients: ["Beet", "Carrot", "Apple", "Ginger"] },
];

const useFeatured = () => {
  const [list, setList] = useState(FALLBACK);
  useEffect(() => {
    http.get("/recipes/featured").then((res) => {
      if (Array.isArray(res.data?.recipes) && res.data.recipes.length) setList(res.data.recipes);
    }).catch(() => {});
  }, []);
  return list;
};

const Cover = () => (
  <motion.div data-testid="recipe-pdf-preview-card" whileHover={{ rotate: -2, y: -6 }} transition={{ type: "spring", stiffness: 180, damping: 18 }} className="relative mx-auto w-full max-w-[380px]">
    <div className="absolute inset-0 translate-x-5 translate-y-5 rotate-[4deg] rounded-[18px] bg-[#181615]" />
    <div className="absolute inset-0 translate-x-2.5 translate-y-2.5 rotate-[2deg] rounded-[18px] bg-[#E3D9CD]" />
    <div className="relative overflow-hidden rounded-[18px] bg-[#F2EAE1] shadow-2xl">
      <img src={IMG.flatlay} alt="Fresh juicing produce flatlay" className="aspect-[4/3] w-full object-cover" />
      <div className="p-7">
        <LogoMark size={30} />
        <p className="font-display mt-5 text-4xl font-light italic leading-none">The Recipe<br />Almanac</p>
        <p className="eyebrow mt-5 text-[0.62rem] text-[#C85A32]">12 cold-pressed recipes · Vol. I</p>
      </div>
    </div>
  </motion.div>
);

export const Almanac = () => {
  const { openCheckout } = useStore();
  const recipes = useFeatured();
  return (
    <section id="almanac" data-testid="recipe-section" className="bg-[#FDFBF7] py-20 sm:py-28">
      <div className="mx-auto grid max-w-[1400px] items-center gap-16 px-5 sm:px-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-5"><Cover /></Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <p className="eyebrow mb-4 text-[#C85A32]">Complimentary with every order</p>
            <h2 className="font-display text-4xl font-light leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">Twelve recipes. <span className="italic">Four chapters.</span> Zero guesswork.</h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4A4543] md:text-lg">From a bright Sunset Citrus Glow to a grounding Canyon Beet Bloom — each created by our Juice Alchemist, Kathryn, and written for the Slowpour's wide chute, with prep tips for maximum yield.</p>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {recipes.map((r, i) => (
              <Reveal key={r.slug} delay={(i % 3) * 0.08}>
                <a href={`/recipes/${r.slug}`} data-testid={`recipe-card-${r.slug}`} className="group block h-full rounded-[22px] border border-[#E3D9CD] bg-white p-6 transition-transform duration-500 hover:-translate-y-1">
                  <span className="mb-5 block h-10 w-10 rounded-full" style={{ background: r.color }} />
                  <p className="eyebrow text-[0.6rem] text-[#6E6661]">{r.chapter}</p>
                  <h3 className="font-display mt-2 text-xl">{r.name}</h3>
                  <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[#6E6661]">{(r.ingredients || []).slice(0, 4).join(" · ")}</p>
                  <p className="mt-4 text-xs font-semibold text-[#C85A32] transition-transform duration-300 group-hover:translate-x-1">Free recipe →</p>
                </a>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 flex flex-wrap items-center gap-4">
            <button data-testid="almanac-buy-button" onClick={openCheckout} className="rounded-full bg-[#181615] px-7 py-3.5 text-sm font-semibold text-[#FDFBF7] transition-[background-color] duration-300 hover:bg-[#C85A32]">Get the juicer + almanac</button>
            <span className="flex items-center gap-2 text-sm text-[#6E6661]"><Lock size={14} /> Unlocks instantly after checkout</span>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
